/**
 * Seven-segment digit geometry for the header readout.
 *
 * Pure, so the glyph table and the outlines can be unit tested without a canvas.
 * A transposed entry here would draw a confidently wrong number, which no amount
 * of looking at one frame of an animation would reliably catch.
 *
 * Segment names are the standard ones, and both the mask bit order and the
 * outline order below follow them:
 *
 *      aaa       bit 0  a  top
 *     f   b      bit 1  b  upper right
 *     f   b      bit 2  c  lower right
 *      ggg       bit 3  d  bottom
 *     e   c      bit 4  e  lower left
 *     e   c      bit 5  f  upper left
 *      ddd       bit 6  g  middle
 */

export const SEG_A = 1 << 0
export const SEG_B = 1 << 1
export const SEG_C = 1 << 2
export const SEG_D = 1 << 3
export const SEG_E = 1 << 4
export const SEG_F = 1 << 5
export const SEG_G = 1 << 6

/** Lit segments for each digit, indexed by value. */
export const DIGIT_MASKS: readonly number[] = [
  SEG_A | SEG_B | SEG_C | SEG_D | SEG_E | SEG_F, // 0
  SEG_B | SEG_C, // 1
  SEG_A | SEG_B | SEG_D | SEG_E | SEG_G, // 2
  SEG_A | SEG_B | SEG_C | SEG_D | SEG_G, // 3
  SEG_B | SEG_C | SEG_F | SEG_G, // 4
  SEG_A | SEG_C | SEG_D | SEG_F | SEG_G, // 5
  SEG_A | SEG_C | SEG_D | SEG_E | SEG_F | SEG_G, // 6
  SEG_A | SEG_B | SEG_C, // 7
  SEG_A | SEG_B | SEG_C | SEG_D | SEG_E | SEG_F | SEG_G, // 8
  SEG_A | SEG_B | SEG_C | SEG_D | SEG_F | SEG_G, // 9
]

/**
 * Outlines for the seven segments in a `w` by `h` cell, as flat x,y point lists
 * in the bit order above.
 *
 * Mitred hexagons rather than rectangles: the angled ends are what make the shape
 * read as a seven-segment display instead of seven floating bars.
 */
export function segmentOutlines(w: number, h: number, thick: number, inset = 1.5): number[][] {
  const t = thick / 2
  const x0 = inset
  const x1 = w - inset
  const yTop = inset
  const yMid = h / 2
  const yBot = h - inset

  const horiz = (y: number): number[] => [
    x0, y, x0 + t, y - t, x1 - t, y - t, x1, y, x1 - t, y + t, x0 + t, y + t,
  ]
  const vert = (x: number, ya: number, yb: number): number[] => [
    x, ya, x + t, ya + t, x + t, yb - t, x, yb, x - t, yb - t, x - t, ya + t,
  ]

  return [
    horiz(yTop),
    vert(x1, yTop, yMid),
    vert(x1, yMid, yBot),
    horiz(yBot),
    vert(x0, yMid, yBot),
    vert(x0, yTop, yMid),
    horiz(yMid),
  ]
}
